import type { TimetablePeriod, TimetableSlot } from "../../services/timetableService";
import { DAYS, clock, levelExcluded } from "./timetableLogic";
import { subjectColor } from "./WeekGrid";

/**
 * A week the way Ugandan schools print it: a row per day, the lesson periods
 * across, and break, lunch and sports as narrow columns that run down the
 * whole week because every class shares them. For one class, the periods it
 * has gone home for (the nursery after 1 pm) read "Home".
 */
export function PeriodGrid({ slots, periods, onOpen, detail, classLevel }: {
  slots: TimetableSlot[];
  /** Every period of the day, lessons and shared times. */
  periods: TimetablePeriod[];
  onOpen: (slot: TimetableSlot) => void;
  /** A line under the subject: the teacher for a class, the class for a teacher. */
  detail: (slot: TimetableSlot) => string;
  /** The class's level, to mark the periods it is not at school for. */
  classLevel?: string | null;
}) {
  const ordered = [...periods].sort((a, b) => a.order - b.order || a.start_time.localeCompare(b.start_time));
  const used = new Set(slots.map((slot) => slot.day_of_week));
  const days = DAYS.map((_, index) => index).filter((index) => index < 5 || used.has(index));
  const lessonAt = new Map(slots.map((slot) => [`${slot.day_of_week}:${slot.period}`, slot]));
  const columns = ["7rem", ...ordered.map((period) => (period.kind === "teaching" ? "minmax(8.5rem, 1fr)" : "2.75rem"))].join(" ");

  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label="Weekly timetable" className="grid rounded-lg border border-gray-200 text-sm" style={{ gridTemplateColumns: columns }}>
        <div role="columnheader" className="border-b border-r border-gray-200 bg-primary-700 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white">Day</div>
        {ordered.map((period) => (
          <div
            key={period.id}
            role="columnheader"
            className={`border-b border-r border-gray-200 px-2 py-2 text-center last:border-r-0 ${period.kind === "teaching" ? "bg-primary-700 text-white" : "bg-primary-800 text-primary-100"}`}
          >
            {period.kind === "teaching" && <div className="text-xs font-semibold">{period.name}</div>}
            <div className="text-[10px] tabular-nums opacity-80">{clock(period.start_time)}–{clock(period.end_time)}</div>
          </div>
        ))}

        {days.map((day, row) => (
          <div key={day} role="row" className="contents">
            <div role="rowheader" className="flex items-center border-b border-r border-gray-200 bg-gray-50 px-3 py-2 font-semibold text-gray-800" style={{ gridRow: row + 2 }}>
              {DAYS[day]}
            </div>
            {ordered.map((period, column) => {
              if (period.kind !== "teaching") {
                // One cell down the whole week, labelled once.
                if (row > 0) return null;
                return (
                  <div
                    key={period.id}
                    role="cell"
                    className="flex items-center justify-center border-b border-r border-gray-200 bg-amber-50/70"
                    style={{ gridColumn: column + 2, gridRow: `2 / span ${days.length}` }}
                  >
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-900/80 [writing-mode:vertical-rl] rotate-180">{levelExcluded(period, classLevel) ? "Home" : period.name}</span>
                  </div>
                );
              }
              const place = { gridColumn: column + 2, gridRow: row + 2 };
              if (levelExcluded(period, classLevel)) {
                return (
                  <div key={period.id} role="cell" className="flex items-center justify-center border-b border-r border-gray-200 bg-gray-50 text-xs italic text-gray-400" style={place}>
                    Home
                  </div>
                );
              }
              const slot = lessonAt.get(`${day}:${period.id}`);
              if (!slot) {
                return <div key={period.id} role="cell" className="border-b border-r border-gray-200" style={place} aria-label="Free" />;
              }
              const color = subjectColor(slot.subject_name);
              return (
                <div key={period.id} role="cell" className="border-b border-r border-gray-200 p-1" style={place}>
                  <button
                    type="button"
                    onClick={() => onOpen(slot)}
                    className={`h-full w-full rounded-md border-l-4 px-2 py-1.5 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${color.bg} ${color.border}`}
                  >
                    <div className={`text-xs font-semibold leading-tight ${color.text}`}>{slot.subject_name}</div>
                    <div className={`mt-0.5 truncate text-[11px] leading-tight ${color.chip}`}>{detail(slot)}</div>
                    {slot.room && <div className="truncate text-[10px] leading-tight text-gray-500">{slot.room}</div>}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
